import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Trash2, Edit2, FileText } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = { subjective: "", objective: "", assessment: "", plan: "", note_date: "" };

export default function ClinicalNotes({ patientId }) {
  const [notes, setNotes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const { data } = await api.get("/clinical-notes", { params: { patient_id: patientId } });
    setNotes(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [patientId]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, note_date: new Date().toISOString().slice(0, 10) });
    setOpen(true);
  };
  const openEdit = (n) => {
    setEditing(n);
    setForm({ subjective: n.subjective || "", objective: n.objective || "",
              assessment: n.assessment || "", plan: n.plan || "",
              note_date: n.note_date || "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, patient_id: patientId };
      if (editing) {
        await api.put(`/clinical-notes/${editing.id}`, body);
        toast.success("Shënimi u përditësua");
      } else {
        await api.post(`/clinical-notes`, body);
        toast.success("Shënimi u shtua");
      }
      setOpen(false); load();
    } catch { toast.error("Gabim"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Fshini këtë shënim?")) return;
    await api.delete(`/clinical-notes/${id}`);
    toast.success("U fshi");
    load();
  };

  return (
    <div className="space-y-6" data-testid="clinical-notes">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Shënime Klinike</div>
          <h3 className="text-2xl font-semibold tracking-tight mt-1">SOAP Notes</h3>
          <p className="mt-1 text-sm text-[#5C5C5C]">Subjektive · Objektive · Vlerësim · Plan — për çdo vizitë klinike.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button onClick={openCreate} className="btn-primary" data-testid="add-note-button">
              <Plus size={16} /> Shënim i Ri
            </button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-2xl">
            <DialogHeader><DialogTitle>{editing ? "Përditëso" : "Shto"} Shënim Klinik</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2">
              <div>
                <Label className="label-eyebrow">Data</Label>
                <Input type="date" value={form.note_date} onChange={(e) => setForm({...form, note_date: e.target.value})} className="mt-2" />
              </div>
              <Section label="S — Subjective" hint="Ankesa, simptomat e pacientit, historiku">
                <Textarea rows={2} value={form.subjective} onChange={(e) => setForm({...form, subjective: e.target.value})}
                          data-testid="soap-subjective" placeholder="P.sh. Dhimbje në dhëmbin 16 prej 3 ditësh, ndjeshmëri ndaj të ftohtit…" />
              </Section>
              <Section label="O — Objective" hint="Gjetje klinike, vëzhgim, ekzaminim">
                <Textarea rows={2} value={form.objective} onChange={(e) => setForm({...form, objective: e.target.value})}
                          data-testid="soap-objective" placeholder="P.sh. Karies i thellë okluzal 16, perkusion (+), tek dhëmbi vital…" />
              </Section>
              <Section label="A — Assessment" hint="Diagnoza">
                <Textarea rows={2} value={form.assessment} onChange={(e) => setForm({...form, assessment: e.target.value})}
                          data-testid="soap-assessment" placeholder="P.sh. Pulpit i pakthyeshëm — dhëmbi 16" />
              </Section>
              <Section label="P — Plan" hint="Trajtimi i planifikuar, medikamente, vizita pasuese">
                <Textarea rows={2} value={form.plan} onChange={(e) => setForm({...form, plan: e.target.value})}
                          data-testid="soap-plan" placeholder="P.sh. Trajtim kanali, vizita e radhës pas 7 ditësh" />
              </Section>
              <DialogFooter>
                <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>Anulo</button>
                <button type="submit" className="btn-primary" data-testid="save-note-button">{editing ? "Përditëso" : "Ruaj"}</button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {notes.length === 0 ? (
        <div className="surface-card p-10 text-center text-sm text-[#5C5C5C]">
          <FileText size={32} className="mx-auto mb-3 text-[#C9C2B2]" />
          Pa shënime klinike. Filloni duke shtuar të parin.
        </div>
      ) : (
        <div className="space-y-4">
          {notes.map((n) => (
            <article key={n.id} className="surface-card p-6" data-testid={`note-${n.id}`}>
              <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
                <div>
                  <div className="text-sm font-semibold">{n.note_date ? new Date(n.note_date).toLocaleDateString("sq-AL", { dateStyle: "full" }) : "—"}</div>
                  <div className="text-xs text-[#5C5C5C]">{n.doctor_name} · krijuar {new Date(n.created_at).toLocaleString("sq-AL")}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(n)} className="p-1.5 rounded hover:bg-[#EAE4D9]">
                    <Edit2 size={14} />
                  </button>
                  <button onClick={() => remove(n.id)} className="p-1.5 rounded hover:bg-[#9E3C3C]/10 text-[#9E3C3C]">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <SoapBlock letter="S" label="Subjective" value={n.subjective} />
                <SoapBlock letter="O" label="Objective" value={n.objective} />
                <SoapBlock letter="A" label="Assessment" value={n.assessment} />
                <SoapBlock letter="P" label="Plan" value={n.plan} />
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function Section({ label, hint, children }) {
  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <div className="text-xs text-[#5C5C5C] mb-1">{hint}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function SoapBlock({ letter, label, value }) {
  return (
    <div className="rounded-lg border border-[#E2DFD8] p-3">
      <div className="flex items-center gap-2 mb-1">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#2C4C3B] text-[#FDFBF7] text-xs font-semibold">{letter}</span>
        <span className="label-eyebrow">{label}</span>
      </div>
      <div className="text-sm whitespace-pre-wrap">{value || <span className="text-[#5C5C5C] italic">— pa shënime</span>}</div>
    </div>
  );
}
